import React, { useMemo, useState } from "react";
import "./ProductReviews.css";

function ProductReviews({ reviews = [] }) {
    const [selectedRating, setSelectedRating] = useState(null);
    const [sortBy, setSortBy] = useState("recent");
    const [showModal, setShowModal] = useState(false);

    const averageRating = useMemo(() => {
        if (!reviews.length) return 0;
        return reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length;
    }, [reviews]);

    const ratingStats = useMemo(() => {
        const stats = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        reviews.forEach((r) => {
            if (r.rating) stats[r.rating]++;
        });
        return stats;
    }, [reviews]);

    const sortedAndFiltered = useMemo(() => {
        let data = [...reviews];

        if (selectedRating) {
            data = data.filter((r) => r.rating === selectedRating);
        }

        if (sortBy === "recent") {
            data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        } else if (sortBy === "high") {
            data.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        } else if (sortBy === "low") {
            data.sort((a, b) => (a.rating || 0) - (b.rating || 0));
        }

        return data;
    }, [reviews, selectedRating, sortBy]);

    // 👉 INITIAL PREVIEW (ONLY 5)
    const previewReviews = useMemo(() => {
        return sortedAndFiltered.slice(0, 5);
    }, [sortedAndFiltered]);

    const ReviewCard = ({ r }) => (
        <div className="product-reviews__card">
            <div className="product-reviews__header">
                <div className="product-reviews__user">
                    <img
                        src={
                            r.client?.clientPhoto ||
                            "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                        }
                        alt="user"
                        className="product-reviews__avatar"
                    />

                    <div>
                        <strong>
                            {r.client?.name || r.reviewBy || "Anonymous"}
                        </strong>

                        <div className="product-reviews__meta">
                            {r.orderId && (
                                <span className="product-reviews__verified">
                                    ✔ Verified Purchase
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                <span className="product-reviews__date">
                    {new Date(r.createdAt).toLocaleDateString()}
                </span>
            </div>

            <div className="product-reviews__rating">
                {"★".repeat(r.rating || 0)}
                {"☆".repeat(5 - (r.rating || 0))}
            </div>

            <p className="product-reviews__text">
                {r.review || "No written review provided."}
            </p>

            {r.adminReply && (
                <div className="product-reviews__reply">
                    <strong>Seller Reply:</strong>
                    <p>{r.adminReply}</p>
                </div>
            )}
        </div>
    );

    if (!reviews.length) {
        return (
            <div className="product-reviews__empty">

                <div className="product-reviews__empty-icon">⭐</div>

                <h2 className="product-reviews__empty-title">
                    No Customer Ratings Yet
                </h2>

                <p className="product-reviews__empty-text">
                    This product has not received any ratings or reviews yet.
                    Ratings will appear here once customers start using the product.
                </p>

                <div className="product-reviews__empty-bar">
                    <div className="product-reviews__empty-bar-fill" />
                </div>

                {/* <p className="product-reviews__empty-subtext">
                    Be the first insights may take time to appear
                </p> */}

            </div>
        );
    }

    return (
        <div className="product-reviews">

            <h1>Ratings & Reviews</h1>

            {/* SUMMARY */}
            <div className="product-reviews__summary">
                <div className="product-reviews__avg">
                    <h2>{averageRating.toFixed(1)}</h2>
                    <p>out of 5</p>
                    <p>{reviews.length} Ratings & Reviews</p>
                </div>

                <div className="product-reviews__bars">
                    {[5, 4, 3, 2, 1].map((star) => (
                        <div
                            key={star}
                            className={`product-reviews__bar-row ${selectedRating === star ? "active" : ""}`}
                            onClick={() => setSelectedRating(star)}
                        >
                            <span>{star}★</span>

                            <div className="product-reviews__bar">
                                <div
                                    className="product-reviews__bar-fill"
                                    style={{
                                        width: `${(ratingStats[star] / reviews.length) * 100}%`,
                                    }}
                                />
                            </div>

                            <span>{ratingStats[star]}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* FILTERS */}
            <div className="product-reviews__filters">
                <select
                    className="product-reviews__select"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                >
                    <option value="recent">Most Recent</option>
                    <option value="high">Top Rated</option>
                    <option value="low">Lowest Rated</option>
                </select>

                {selectedRating && (
                    <button
                        className="product-reviews__clear"
                        onClick={() => setSelectedRating(null)}
                    >
                        Clear Filter ✖
                    </button>
                )}
            </div>

            {/* INITIAL 5 REVIEWS */}
            <div className="product-reviews__list">
                {previewReviews.map((r) => (
                    <ReviewCard key={r.id} r={r} />
                ))}
            </div>

            {/* SHOW MORE */}
            {sortedAndFiltered.length > 5 && (
                <button
                    className="product-reviews__show-more"
                    onClick={() => setShowModal(true)}
                >
                    Show all {sortedAndFiltered.length} reviews
                </button>
            )}

            {/* MODAL */}
            {showModal && (
                <div className="product-reviews__modal-overlay">
                    <div className="product-reviews__modal">

                        {/* HEADER */}
                        <div className="product-reviews__modal-header">
                            <h2>All Reviews</h2>

                            <button
                                className="product-reviews__modal-close"
                                onClick={() => setShowModal(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {/* ⭐ SUMMARY (MISSING BEFORE — NOW RESTORED) */}
                        <div className="product-reviews__summary">
                            <div className="product-reviews__avg">
                                <h2>{averageRating.toFixed(1)}</h2>
                                <p>out of 5</p>
                                <p>{reviews.length} Ratings & Reviews</p>
                            </div>

                            <div className="product-reviews__bars">
                                {[5, 4, 3, 2, 1].map((star) => (
                                    <div
                                        key={star}
                                        className={`product-reviews__bar-row ${selectedRating === star ? "active" : ""
                                            }`}
                                        onClick={() => setSelectedRating(star)}
                                    >
                                        <span>{star}★</span>

                                        <div className="product-reviews__bar">
                                            <div
                                                className="product-reviews__bar-fill"
                                                style={{
                                                    width: `${(ratingStats[star] / reviews.length) * 100
                                                        }%`,
                                                }}
                                            />
                                        </div>

                                        <span>{ratingStats[star]}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* 🔽 FILTERS */}
                        <div className="product-reviews__modal-filters">
                            <select
                                className="product-reviews__select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="recent">Most Recent</option>
                                <option value="high">Top Rated</option>
                                <option value="low">Lowest Rated</option>
                            </select>

                            {selectedRating && (
                                <button
                                    className="product-reviews__clear"
                                    onClick={() => setSelectedRating(null)}
                                >
                                    Clear Rating ✖
                                </button>
                            )}
                        </div>

                        {/* 🧾 BODY */}
                        <div className="product-reviews__modal-body">
                            {sortedAndFiltered.map((r) => (
                                <ReviewCard key={r.id} r={r} />
                            ))}
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}

export default ProductReviews;